import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col } from "react-bootstrap";
import {
  fetchThreeDayPTMemberDetailsData,
  fetchThreeDayPTSessionStatsData,
} from "../../../redux/plans-reducers/plansSlice";
import CommonStatCard from "../pt-flow-new/CommonStatCard";
import PaymentLinkTable from "../../payment-link-system/PaymentLinkTable";
import ReactVortoxSpinner from "../../common-components/ReactVortoxSpinner";
import { convertStraight } from "../../common-data/Converts";
import {
  PendingIcon,
  CompletedIcon,
  ClockIcon,
  DiagramIcon,
} from "../../../images/icons/icons";
import { isManagerRole as checkIsManagerRole } from "../../common-data/Conversions";

const ThreeDayMemberDetailsTab = ({ userInfoData }) => {
  const dispatch = useDispatch();
  const {
    threeDayPTSessionStats,
    threeDayPTSessionStatsLoading,
    threeDayPTMemberDetails,
    threeDayPTMemberDetailsLoading,
  } = useSelector((state) => state.plans);

  const isAdmin = userInfoData?.account_type === "admin";
  const isMarketing = userInfoData?.account_type === "marketing";
  const isManagerRole = checkIsManagerRole(userInfoData);

  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);
  const [selectedStatus, setSelectedStatus] = useState("ongoing");

  const gymFilter =
    !isAdmin && !isMarketing && !isManagerRole
      ? userInfoData?.gym_id || userInfoData?.user_id || userInfoData?.uid || ""
      : "";

  // Fetch stats on component mount
  useEffect(() => {
    const filters = {
      ...(gymFilter && { gym_id: gymFilter }),
    };
    dispatch(
      fetchThreeDayPTSessionStatsData({
        filters,
        token: userInfoData?.token,
      })
    );
  }, [gymFilter, userInfoData?.token]);

  // Fetch member details when status or pagination changes
  useEffect(() => {
    const filters = {
      status: selectedStatus,
      page: isPage + 1,
      limit: pageLimit,
      ...(gymFilter && { gym_id: gymFilter }),
    };
    dispatch(
      fetchThreeDayPTMemberDetailsData({
        filters,
        token: userInfoData?.token,
      })
    );
  }, [selectedStatus, isPage, pageLimit, gymFilter, userInfoData?.token]);

  const stats = threeDayPTSessionStats?.data || {};
  const memberData = threeDayPTMemberDetails?.data || [];
  const pagination = threeDayPTMemberDetails?.pagination || [];

  const statsCardsData = [
    {
      tagName: "ongoing",
      cardLabel: "Ongoing",
      value: stats.ongoing || 0,
      classname: `${
        selectedStatus === "ongoing" ? "selected" : ""
      } gradient-orange`,
      icon: <PendingIcon />,
      clickable: true,
    },
    {
      tagName: "completed",
      cardLabel: "Completed",
      value: stats.completed || 0,
      classname: `${
        selectedStatus === "completed" ? "selected" : ""
      } gradient-green`,
      icon: <CompletedIcon />,
      clickable: true,
    },
    {
      tagName: "upcoming",
      cardLabel: "Upcoming",
      value: stats.upcoming || 0,
      classname: `${
        selectedStatus === "upcoming" ? "selected" : ""
      } gradient-blue`,
      icon: <ClockIcon />,
      clickable: true,
    },
    {
      tagName: "total",
      cardLabel: "Total",
      value: stats.total || 0,
      classname: "gradient-purple",
      icon: <DiagramIcon />,
      clickable: false,
    },
  ];

  const handleCardClick = (status) => {
    setSelectedStatus(status);
    setIsPage(0);
  };

  const tableColumns = [
    {
      dataField: "member_name",
      text: "Member Name",
      formatter: (_, row) => {
        return (
          <div className="tb-data minWidth150">
            <span className="bold">{row?.member_name || "NA"}</span>
            <br />
            <small style={{ textTransform: "lowercase", fontWeight: "500" }}>
              {row?.member_mobile || ""}
              <br />
              {row?.member_email || ""}
            </small>
          </div>
        );
      },
    },
    {
      dataField: "gym_name",
      text: "Gym Name",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth150">{row?.gym_name || "NA"}</span>
        );
      },
    },
    {
      dataField: "trainer_name",
      text: "Trainer Name",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth150">
            {row?.trainer_name || "NA"}
          </span>
        );
      },
    },
    {
      dataField: "last_session",
      text: "Last Session",
      formatter: (_, row) => {
        return (
          <div className="tb-data minWidth150">
            <span>{row?.last_session_date || "NA"}</span>
            <br />
            <small>{row?.last_session_time || ""}</small>
            <br />
            <small
              className={`status-badge ${
                row?.last_session_status === "completed"
                  ? "status-success"
                  : row?.last_session_status === "pending"
                  ? "status-warning"
                  : ""
              }`}
            >
              {row?.last_session_status || ""}
            </small>
          </div>
        );
      },
    },
    {
      dataField: "member_last_checkin",
      text: "Last Check-in",
      formatter: (_, row) => {
        return (
          <span className="tb-data">{row?.member_last_checkin || "NA"}</span>
        );
      },
    },
    {
      dataField: "session_consumption",
      text: "Sessions",
      formatter: (_, row) => {
        return (
          <div className="tb-data">
            <span>Consumed: {row?.pt_session_consumption || 0}</span>
            <br />
            <small>Time: {row?.pt_session_time || "NA"}</small>
          </div>
        );
      },
    },
    {
      dataField: "plan_duration",
      text: "Duration",
      formatter: (_, row) => {
        return (
          <div className="tb-data">
            <span>PT: {row?.pt_plan_duration || 0} days</span>
            <br />
            <small>Membership: {row?.membership_duration || 0} days</small>
          </div>
        );
      },
    },
    {
      dataField: "subscription_dates",
      text: "Subscription Period",
      formatter: (_, row) => {
        return (
          <div className="tb-data minWidth150">
            <span>
              Start:{" "}
              {row?.subscription_start_date
                ? convertStraight(row.subscription_start_date)
                : "NA"}
            </span>
            <br />
            <small>
              End:{" "}
              {row?.subscription_expire_date
                ? convertStraight(row.subscription_expire_date)
                : "NA"}
            </small>
          </div>
        );
      },
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (_, row) => {
        return (
          <div className="tb-data">
            <span
              className={`status-badge ${
                row?.ptplanstatus === "active"
                  ? "status-success"
                  : row?.ptplanstatus === "expired"
                  ? "status-danger"
                  : "status-warning"
              }`}
            >
              PT: {row?.ptplanstatus || "NA"}
            </span>
            <br />
            <small
              className={`status-badge ${
                row?.membership_status === "active"
                  ? "status-success"
                  : row?.membership_status === "expired"
                  ? "status-danger"
                  : "status-warning"
              }`}
            >
              Membership: {row?.membership_status || "NA"}
            </small>
          </div>
        );
      },
    },
  ];

  return (
    <div className="three-day-member-details-tab">
      {/* Stats Cards */}
      <Row className="mb-4">
        <Col>
          {threeDayPTSessionStatsLoading ? (
            <div className="text-center py-4">
              <ReactVortoxSpinner />
            </div>
          ) : (
            <div className="stats-cards-container">
              {statsCardsData.map((card) => (
                <div key={card.tagName}>
                  <CommonStatCard
                    tagName={card.tagName}
                    cardLabel={card.cardLabel}
                    value={card.value}
                    icon={card.icon}
                    onClickCard={card.clickable ? handleCardClick : null}
                    classname={card.classname}
                  />
                </div>
              ))}
            </div>
          )}
        </Col>
      </Row>

      {/* Member Details Table */}
      <Row>
        <Col>
          {threeDayPTMemberDetailsLoading ? (
            <div className="text-center py-4">
              <ReactVortoxSpinner />
            </div>
          ) : (
            <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mt30">
              <PaymentLinkTable
                tableData={memberData}
                tableColumns={tableColumns}
                tablePagination={pagination}
                isPage={isPage}
                setIsPage={setIsPage}
                pageLimit={pageLimit}
                setPageLimit={setPageLimit}
                isFirstColSticky={false}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ThreeDayMemberDetailsTab;
